// Cursed Iron Effect (Heavy, Brutal, Magical Armor Fusion)
// Features (Iteration 3 - Sliced Dynamic Assembly): 
// 1. MATERIALIZE (0-1500ms): Dark shards spiral inwards. 5 distinct SVG armor plates slam together over the avatar.
// 2. INFUSION (1500-2500ms): Assembled SVG slowly pulses, while expanding a glowing base aura. Emits sparks/vapors.
// 3. PULSE (2500-3000ms): Massive shockwave of cyan/orange energy detonates outward.
// 4. DISSIPATE (3000-3800ms): Armor cools to pitch black and smoothly fades out.

import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 0.8, // The SVG slices are 240x240, scale them appropriately to fit over an 85px radius avatar
    materializeDuration: 1500, // Slower, heavier assembly
    infusionDuration: 1000,
    pulseDuration: 500,
    dissipateDuration: 800,
    paths: {
        base: '/assets/effects/cursed_iron/cursed_iron_base.svg',
        tl: '/assets/effects/cursed_iron/cursed_iron_tl.svg',
        tr: '/assets/effects/cursed_iron/cursed_iron_tr.svg',
        bl: '/assets/effects/cursed_iron/cursed_iron_bl.svg',
        br: '/assets/effects/cursed_iron/cursed_iron_br.svg',
        center: '/assets/effects/cursed_iron/cursed_iron_center.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

// Preload all pieces
Object.values(CONFIG.paths).forEach(src => getSprite(src));

// Represents a distinct chunk of the SVG armor
class ArmorPiece {
    src: string;
    startX: number; startY: number;
    delay: number; duration: number;
    rotationStart: number;

    constructor(src: string, angleOffset: number, delayOffset: number) {
        this.src = src;

        // Start far off screen matching their general target direction
        const dist = 600 + Math.random() * 400;
        this.startX = Math.cos(angleOffset) * dist;
        this.startY = Math.sin(angleOffset) * dist;

        this.delay = delayOffset;
        this.duration = 600 + Math.random() * 200; // Snap in quickly towards the end
        this.rotationStart = (Math.random() - 0.5) * Math.PI; // Spin as it comes in
    }

    draw(ctx: CanvasRenderingContext2D, effT: number, tMat: number, fadePhase: number) {
        let alpha = 1;
        let pX = 0; let pY = 0; let rot = 0;

        // Assembly Phase
        if (effT < tMat) {
            if (effT < this.delay) return; // Wait to spawn

            let progress = (effT - this.delay) / this.duration;
            if (progress > 1) progress = 1;

            // Heavy explosive easing (starts fast, slams into a stop)
            const ease = 1 - Math.pow(1 - progress, 5);

            pX = this.startX * (1 - ease);
            pY = this.startY * (1 - ease);
            rot = this.rotationStart * (1 - ease);

            // Fade in gently at the very start of its flight
            alpha = Math.min(1, progress * 4);
        } else {
            // Fade Phase
            if (fadePhase > 0) {
                alpha = 1 - fadePhase;
            }
        }

        const img = getSprite(this.src);
        if (img.complete && img.naturalWidth > 0) {
            ctx.save();
            ctx.translate(pX, pY);
            ctx.rotate(rot);

            ctx.globalAlpha = alpha;
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
            ctx.restore();
        }
    }
}

class IronShard {
    startX: number; startY: number;
    delay: number; duration: number;
    lifeTime: number = 0;

    // Geometry
    radiusScale: number; // How far from center it locks in
    targetAngle: number; // Final angle around the avatar
    orbitOffset: number; // How much it orbits before locking

    // Polygon points to look like armor plating
    points: { x: number, y: number }[];

    constructor() {
        const startAngle = Math.random() * Math.PI * 2;
        const dist = 600 + Math.random() * 600;
        this.startX = Math.cos(startAngle) * dist;
        this.startY = Math.sin(startAngle) * dist - 200;

        this.delay = Math.random() * 500;
        this.duration = 600 + Math.random() * 400;

        this.targetAngle = Math.random() * Math.PI * 2;
        this.orbitOffset = (Math.random() > 0.5 ? 1 : -1) * (Math.PI + Math.random() * Math.PI * 2);

        this.points = [];
        const numPts = 3 + Math.floor(Math.random() * 3);
        const sizeBase = 20 + Math.random() * 30;
        for (let i = 0; i < numPts; i++) {
            const a = (i / numPts) * Math.PI * 2;
            const r = sizeBase * (0.5 + Math.random() * 0.5);
            this.points.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
        }

        // Lock slightly behind the SVG armor chunks
        this.radiusScale = 70 + Math.random() * 40;
    }

    draw(ctx: CanvasRenderingContext2D, dt: number, targetX: number, targetY: number, fadePhase: number = 0) {
        if (this.delay > 0) {
            this.delay -= dt;
            return;
        }

        this.lifeTime += dt;
        let progress = this.lifeTime / this.duration;
        if (progress > 1) progress = 1;

        const ease = 1 - Math.pow(1 - progress, 4);

        const currentRadius = (1 - ease) * 800 + ease * this.radiusScale;
        const currentAngle = this.targetAngle + this.orbitOffset * (1 - ease);

        const x = targetX + this.startX * (1 - ease) + Math.cos(currentAngle) * currentRadius * ease;
        const y = targetY + this.startY * (1 - ease) + Math.sin(currentAngle) * currentRadius * ease;
        const rotation = currentAngle + Math.PI / 2;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rotation);

        if (fadePhase > 0) {
            ctx.globalAlpha = 1 - fadePhase;
        }
        ctx.fillStyle = '#18181b';
        ctx.strokeStyle = '#3f3f46';

        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.points[0].y);
        for (let i = 1; i < this.points.length; i++) {
            ctx.lineTo(this.points[i].x, this.points[i].y);
        }
        ctx.closePath();

        ctx.fill();
        ctx.stroke();

        ctx.restore();
    }
}

class CinderSpark {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;
    isCyan: boolean;

    constructor(x: number, y: number) {
        this.x = x + (Math.random() - 0.5) * 100;
        this.y = y + (Math.random() - 0.5) * 150;

        this.isCyan = Math.random() > 0.5;
        this.color = this.isCyan ? '#22d3ee' : '#f97316'; // Cyan or Orange

        // Venting outwards and upwards
        this.vx = (Math.random() - 0.5) * 6;
        this.vy = -2 - Math.random() * 5;
        this.size = 2 + Math.random() * 4;
        this.life = 1.0;
    }

    update(dt: number) {
        this.x += this.vx * dt * 0.06;
        this.y += this.vy * dt * 0.06;
        this.life -= dt * 0.0015;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = this.life;
        ctx.globalCompositeOperation = 'screen';

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;

        ctx.shadowColor = this.color;
        ctx.shadowBlur = 10;

        ctx.fill();
        ctx.restore();
    }
}

class CurseVapor {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;

    constructor(x: number, y: number) {
        this.x = x + (Math.random() - 0.5) * 150;
        this.y = y + 50 + (Math.random() - 0.5) * 100;
        this.vx = (Math.random() - 0.5) * 2;
        this.vy = -1 - Math.random() * 2;
        this.size = 40 + Math.random() * 60;
        this.life = 1.0;
        this.color = Math.random() > 0.5 ? 'rgba(8, 145, 178, 0.4)' : 'rgba(194, 65, 12, 0.4)'; // Dark cyan/orange
    }

    update(dt: number) {
        this.x += this.vx * dt * 0.06;
        this.y += this.vy * dt * 0.06;
        this.size += dt * 0.05;
        this.life -= dt * 0.001;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.life * 0.6);
        ctx.globalCompositeOperation = 'screen';

        const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
        grad.addColorStop(0, this.color);
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }
}

export type CursedIronEffect = {
    kind: 'cursedIron'
    t: number
    tx: number; ty: number;
    isOpponent: boolean;
    data?: {
        phase: 'MATERIALIZE' | 'INFUSION' | 'PULSE' | 'DISSIPATE'
        shards: IronShard[]
        pieces: ArmorPiece[]
        sparks: CinderSpark[]
        vapors: CurseVapor[]
    }
}

export const createCursedIronEffect = (tx: number, ty: number, isOpponent: boolean): CursedIronEffect => ({
    kind: 'cursedIron', t: 0, tx, ty, isOpponent
});

export const renderCursedIronEffect = (
    eff: CursedIronEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        const shards: IronShard[] = [];
        for (let i = 0; i < 45; i++) {
            shards.push(new IronShard());
        }

        // SVG slices assigned staggered delays and starting angles
        const pieces: ArmorPiece[] = [
            // Center visor crashes in last from straight above
            new ArmorPiece(CONFIG.paths.center, -Math.PI / 2, 700),
            // Top Left & Right crash in slightly earlier
            new ArmorPiece(CONFIG.paths.tl, -Math.PI * 0.75, 500),
            new ArmorPiece(CONFIG.paths.tr, -Math.PI * 0.25, 500),
            // Bottom plates arrive first from the sides/bottom
            new ArmorPiece(CONFIG.paths.bl, Math.PI * 0.75, 400),
            new ArmorPiece(CONFIG.paths.br, Math.PI * 0.25, 400),
        ];

        eff.data = {
            phase: 'MATERIALIZE',
            shards,
            pieces,
            sparks: [],
            vapors: []
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tMat = CONFIG.materializeDuration;
    const tInf = tMat + CONFIG.infusionDuration;
    const tPulse = tInf + CONFIG.pulseDuration;
    const tTotal = tPulse + CONFIG.dissipateDuration;

    if (eff.t < tMat) d.phase = 'MATERIALIZE';
    else if (eff.t < tInf) d.phase = 'INFUSION';
    else if (eff.t < tPulse) d.phase = 'PULSE';
    else d.phase = 'DISSIPATE';

    ctx.save();

    let armorScale = CONFIG.scale;
    let baseAlpha = 0;
    let brightness = 1;
    let shockwaveRadius = 0;
    let armorFade = 0;
    let screenShake = 0;

    if (d.phase === 'MATERIALIZE') {
        const p = eff.t / tMat;
        // Base aura glows up as shards form
        baseAlpha = Math.pow(p, 2);

        if (eff.t > tMat - 200) screenShake = 5; // Heavy tremor at the end of assembly 

    } else if (d.phase === 'INFUSION') {
        baseAlpha = 1;
        const p = (eff.t - tMat) / CONFIG.infusionDuration;

        armorScale = CONFIG.scale * (1 + Math.sin(p * Math.PI) * 0.05);
        brightness = 1 + Math.sin(p * Math.PI * 4) * 0.3;
        screenShake = 2; // Constant low rumbpe

        if (Math.random() < 0.6) d.sparks.push(new CinderSpark(eff.tx, eff.ty));
        if (Math.random() < 0.3) d.vapors.push(new CurseVapor(eff.tx, eff.ty));

    } else if (d.phase === 'PULSE') {
        baseAlpha = 1;
        const p = (eff.t - tInf) / CONFIG.pulseDuration;

        armorScale = CONFIG.scale * (1 + p * 0.15);
        brightness = 1.8 - p * 0.8;
        shockwaveRadius = Math.pow(p, 0.5) * 600;
        screenShake = 15; // Massive tremor

        if (Math.random() < 0.4) d.sparks.push(new CinderSpark(eff.tx, eff.ty));

    } else if (d.phase === 'DISSIPATE') {
        const p = (eff.t - tPulse) / CONFIG.dissipateDuration;

        brightness = 1 - p;
        armorFade = Math.pow(p, 0.8);
        baseAlpha = 1 - Math.pow(p, 2);

        if (Math.random() < 0.6) {
            d.sparks.push(new CinderSpark(eff.tx, eff.ty));
            d.sparks[d.sparks.length - 1].color = '#27272a';
            d.sparks[d.sparks.length - 1].isCyan = false;
        }
    }

    // UPDATE & DRAW BACKGROUND SHARDS
    ctx.save();
    if (d.phase === 'PULSE') {
        const p = (eff.t - tInf) / CONFIG.pulseDuration;
        ctx.translate(eff.tx, eff.ty);
        ctx.scale(1 + p * 0.15, 1 + p * 0.15);
        ctx.translate(-eff.tx, -eff.ty);
    }
    d.shards.forEach(s => s.draw(ctx, dt, eff.tx, eff.ty, armorFade));
    ctx.restore();

    // UPDATE & DRAW ENVIRONMENTAL PARTICLES 
    d.vapors.forEach(v => { v.update(dt); v.draw(ctx); });
    d.sparks.forEach(s => { s.update(dt); s.draw(ctx); });

    d.vapors = d.vapors.filter(v => v.life > 0);
    d.sparks = d.sparks.filter(s => s.life > 0);

    // DRAW SHOCKWAVE (During Pulse)
    if (shockwaveRadius > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(eff.tx, eff.ty);

        const p = (eff.t - tInf) / CONFIG.pulseDuration;
        ctx.globalAlpha = 1 - Math.pow(p, 2);

        ctx.beginPath();
        ctx.arc(0, 0, shockwaveRadius, 0, Math.PI * 2);
        ctx.strokeStyle = '#22d3ee';
        ctx.lineWidth = 15 * (1 - p);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, shockwaveRadius * 0.8, 0, Math.PI * 2);
        ctx.strokeStyle = '#ea580c';
        ctx.lineWidth = 35 * (1 - p);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, shockwaveRadius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${(1 - p) * 0.3})`;
        ctx.fill();

        ctx.restore();
    }

    // DRAW DYNAMIC SVG ARMOR Assembly
    ctx.save();

    if (screenShake > 0) {
        ctx.translate((Math.random() - 0.5) * screenShake, (Math.random() - 0.5) * screenShake);
    }
    ctx.translate(eff.tx, eff.ty);
    if (eff.isOpponent) ctx.rotate(Math.PI);

    // 1. Draw Inner Glowing Base
    if (baseAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = baseAlpha;
        const bImg = getSprite(CONFIG.paths.base);
        if (bImg.complete && bImg.naturalWidth > 0) {
            ctx.scale(armorScale, armorScale);
            ctx.drawImage(bImg, -bImg.naturalWidth / 2, -bImg.naturalHeight / 2, bImg.naturalWidth, bImg.naturalHeight);
        }
        ctx.restore();
    }

    // 2. Draw Assembling Armor Chunks
    ctx.scale(armorScale, armorScale);
    if (brightness !== 1) ctx.filter = `brightness(${brightness})`;

    // Sort so center renders last on top
    d.pieces.forEach(piece => piece.draw(ctx, eff.t, tMat, armorFade));

    ctx.restore(); // Undo avatar transform
    ctx.restore(); // Undo global save

    return eff.t < tTotal;
};
